import React, {useMemo} from 'react';
import {Button, DatePicker, message} from 'antd';
import type {Dayjs} from 'dayjs';
import dayjs from 'dayjs';
import {useTranslation} from 'react-i18next';
import {useQuery} from '@apollo/client';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';
import {getSmartLabelClassName} from '../../../../utils/labelWidthUtils';
import {GET_SORTER_LISTING} from '../../sorter/graphql/queries/getSorterListing';
import {GET_SORTER_PLAN_LISTING} from '../../sorterPlan/graphql/queries/getSorterPlanListing';
import {SorterSortingLogFiltersInput} from '../graphql/queries/getSorterSortingLogListing';
import {useWarehouseContext} from '../../../../contexts/WarehouseContext';
import {useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName} from '../../../../utils/languageUtils';

interface SorterSortingLogFilterProps {
    filters: SorterSortingLogFiltersInput;
    setFilters: (filters: SorterSortingLogFiltersInput) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
}

const SorterSortingLogFilter: React.FC<SorterSortingLogFilterProps> = ({
    filters,
    setFilters,
    handleFilter,
    handleResetFilters,
}) => {
    const {t} = useTranslation();
    const {currentWarehouse} = useWarehouseContext();

    const {data: sorterData} = useQuery(GET_SORTER_LISTING, {
        variables: {
            filters: {warehouseId: currentWarehouse?.id},
            pageRequest: {page: 1, size: 1000},
        },
        skip: !currentWarehouse?.id,
    });

    const {data: sorterPlanData} = useQuery(GET_SORTER_PLAN_LISTING, {
        variables: {
            // warehouseId 始终要传：接口按仓鉴权，缺了会报 warehouseIdRequired；
            // 后端把 warehouseId 转成本仓分拣机集合再与 sorterId 取交集，选了分拣机即只返回该分拣机的方案
            filters: {warehouseId: currentWarehouse?.id, sorterId: filters.sorterId},
            pageRequest: {page: 1, size: 1000},
        },
        skip: !currentWarehouse?.id,
    });

    const sorterOptions = useMemo(() => {
        const all = [{value: '', label: t('common.all')}];
        if (!sorterData?.getSorterListing?.content) return all;
        return [
            ...all,
            ...sorterData.getSorterListing.content.map((s: any) => ({
                value: String(s.id),
                label: `${s.sorterCode} - ${s.sorterName}`,
            })),
        ];
    }, [sorterData, t]);

    const sorterPlanOptions = useMemo(() => {
        const all = [{value: '', label: t('common.all')}];
        if (!sorterPlanData?.getSorterPlanListing?.content) return all;
        return [
            ...all,
            ...sorterPlanData.getSorterPlanListing.content.map((p: any) => ({
                value: String(p.id),
                label: p.sorterPlanName || String(p.id),
            })),
        ];
    }, [sorterPlanData, t]);

    const sourceTypeOptions = useMemo(() => [
        {value: '1', label: t('wms.sorterSortingLog.sourceType.getConfig')},
        {value: '2', label: t('wms.sorterSortingLog.sourceType.dwsScan')},
        {value: '3', label: t('wms.sorterSortingLog.sourceType.crossBeltScan')},
        {value: '4', label: t('wms.sorterSortingLog.sourceType.dropChute')},
        {value: '5', label: t('wms.sorterSortingLog.sourceType.lockChute')},
        {value: '6', label: t('wms.sorterSortingLog.sourceType.unlock')},
        {value: '7', label: t('wms.sorterSortingLog.sourceType.fullChute')},
        {value: '8', label: t('wms.sorterSortingLog.sourceType.runStatus')},
        {value: '9', label: t('wms.sorterSortingLog.sourceType.imageUpload')},
        {value: '10', label: t('wms.sorterSortingLog.sourceType.operatorUpdate')},
        {value: '11', label: t('wms.sorterSortingLog.sourceType.linearSorterLabelPrint')},
        {value: '12', label: t('wms.sorterSortingLog.sourceType.reprint')},
        {value: '13', label: t('wms.sorterSortingLog.sourceType.printBag')},
        {value: '14', label: t('wms.sorterSortingLog.sourceType.batchUnlock')},
        {value: '15', label: t('wms.sorterSortingLog.sourceType.batchLockChute')},
        {value: '16', label: t('wms.sorterSortingLog.sourceType.batchReset')},
        // code 20 非连续（17/18/19 留空），与后端 SourceType.AUTO_UNLOCK 对齐
        {value: '20', label: t('wms.sorterSortingLog.sourceType.autoUnlock')},
    ], [t]);

    const labelSorterId = String(t('wms.sorterSortingLog.filters.sorterId'));
    const labelSorterPlanId = String(t('wms.sorterSortingLog.filters.sorterPlanId'));
    const labelSourceType = String(t('wms.sorterSortingLog.filters.sourceType'));
    const labelTrackingNumber = String(t('wms.sorterSortingLog.filters.trackingNumber'));
    const labelBagNumber = String(t('wms.sorterSortingLog.filters.bagNumber'));
    const labelChuteCode = String(t('wms.sorterSortingLog.filters.sorterSortingChuteCode'));
    const labelUuid = String(t('wms.sorterSortingLog.filters.uuid'));
    const labelExpectChuteCodes = String(t('wms.sorterSortingLog.filters.expectSorterSortingChuteCodes'));
    const labelSortingTaskId = String(t('wms.sorterSortingLog.filters.sortingTaskId'));
    const labelCreatedAt = String(t('wms.sorterSortingLog.filters.createdAt'));

    const col1Labels = [labelSorterId, labelTrackingNumber, labelExpectChuteCodes];
    const col2Labels = [labelSorterPlanId, labelBagNumber, labelChuteCode];
    // 各列标签参与宽度自适应计算，新增输入项必须把标签加进对应列，否则该列标签可能被截断
    const col3Labels = [labelSourceType, labelUuid, labelSortingTaskId];

    const col1SpanClassName = getSmartLabelClassName(col1Labels);
    const col2SpanClassName = getSmartLabelClassName(col2Labels);
    const col3SpanClassName = getSmartLabelClassName(col3Labels);

    const containerClassName = useFilterContainerClassName(undefined, true);
    const labelClassName = useFilterLabelClassName(col1SpanClassName);
    const inputContainerClassName = useFilterInputContainerClassName();

    const dayjsRange: [Dayjs | null, Dayjs | null] | null = useMemo(() => {
        if (!filters.createdAtStart && !filters.createdAtEnd) return null;
        return [
            filters.createdAtStart ? dayjs.unix(filters.createdAtStart) : null,
            filters.createdAtEnd ? dayjs.unix(filters.createdAtEnd) : null,
        ];
    }, [filters.createdAtStart, filters.createdAtEnd]);

    const handleCreatedAtChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        setFilters({
            ...filters,
            createdAtStart: dates?.[0] ? dates[0].unix() : undefined,
            createdAtEnd: dates?.[1] ? dates[1].unix() : undefined,
        });
    };

    return (
        <div className="mb-2 pl-0 py-2">
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelSorterId}
                        value={filters.sorterId !== undefined ? String(filters.sorterId) : ''}
                        onChange={(v) => setFilters({
                            ...filters,
                            sorterId: v ? parseInt(v) : undefined,
                            sorterPlanId: undefined,
                        })}
                        options={sorterOptions}
                        spanClassName={col1SpanClassName}
                    />
                </div>

                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelSorterPlanId}
                        value={filters.sorterPlanId !== undefined ? String(filters.sorterPlanId) : ''}
                        onChange={(v) => setFilters({...filters, sorterPlanId: v ? parseInt(v) : undefined})}
                        options={sorterPlanOptions}
                        spanClassName={col2SpanClassName}
                    />
                </div>

                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelSourceType}
                        mode="multiple"
                        maxTagCount="responsive"
                        // ParcelFilterStyle 内部把 option.value 统一 String 化，这里也要给字符串数组，否则选中项回显不上
                        value={(filters.sourceTypes ?? []).map(String)}
                        // 清空（undefined 而非空数组）才能让后端的 takeIf { isNotEmpty() } 判定为不过滤
                        onChange={(v: string[]) => setFilters({
                            ...filters,
                            sourceTypes: v?.length ? v.map(Number) : undefined,
                        })}
                        options={sourceTypeOptions}
                        spanClassName={col3SpanClassName}
                    />
                </div>

                <div className="min-w-0">
                    <InputFilterItem
                        label={labelTrackingNumber}
                        value={filters.trackingNumber || ''}
                        onChange={(e) => setFilters({...filters, trackingNumber: e.target.value || undefined})}
                        spanClassName={col1SpanClassName}
                    />
                </div>

                <div className="min-w-0">
                    <InputFilterItem
                        label={labelBagNumber}
                        value={filters.bagNumber || ''}
                        onChange={(e) => setFilters({...filters, bagNumber: e.target.value || undefined})}
                        spanClassName={col2SpanClassName}
                    />
                </div>

                <div className="min-w-0">
                    <InputFilterItem
                        label={labelUuid}
                        value={filters.uuid || ''}
                        onChange={(e) => setFilters({...filters, uuid: e.target.value || undefined})}
                        spanClassName={col3SpanClassName}
                    />
                </div>

                <div className="min-w-0">
                    <InputFilterItem
                        label={labelExpectChuteCodes}
                        value={filters.expectSorterSortingChuteCodes || ''}
                        onChange={(e) => setFilters({...filters, expectSorterSortingChuteCodes: e.target.value || undefined})}
                        spanClassName={col1SpanClassName}
                    />
                </div>

                <div className="min-w-0">
                    <InputFilterItem
                        label={labelChuteCode}
                        value={filters.sorterSortingChuteCode || ''}
                        onChange={(e) => setFilters({...filters, sorterSortingChuteCode: e.target.value || undefined})}
                        spanClassName={col2SpanClassName}
                    />
                </div>

                <div className="min-w-0">
                    <InputFilterItem
                        label={labelSortingTaskId}
                        value={filters.sortingTaskId || ''}
                        onChange={(e) => setFilters({...filters, sortingTaskId: e.target.value || undefined})}
                        spanClassName={col3SpanClassName}
                    />
                </div>

                <div className="col-span-2 min-w-0">
                    <div className={containerClassName}>
                        <span className={labelClassName}>
                            <span style={{color: '#ff4d4f', marginRight: 2}}>*</span>{labelCreatedAt}
                        </span>
                        <div className={inputContainerClassName}>
                            <DatePicker.RangePicker
                                value={dayjsRange}
                                onChange={handleCreatedAtChange}
                                showTime={{format: 'HH:mm:ss'}}
                                format="YYYY-MM-DD HH:mm:ss"
                                className="w-full"
                                style={{width: '100%'}}
                                size="middle"
                                allowClear={false}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-end gap-3 mt-4">
                <Button
                    type="primary"
                    onClick={() => {
                        if (!filters.createdAtStart || !filters.createdAtEnd) {
                            message.warning(t('wms.sorterSortingLog.validation.createdAtRequired'));
                            return;
                        }
                        handleFilter();
                    }}
                    className="font-semibold min-w-[80px]"
                >
                    {t('wms.sorterSortingLog.buttons.filter')}
                </Button>
                <Button onClick={handleResetFilters} className="font-semibold min-w-[80px]">
                    {t('wms.sorterSortingLog.buttons.reset')}
                </Button>
            </div>
        </div>
    );
};

export default SorterSortingLogFilter;
